import { cn } from "../../utils/cn";

import type { CSSProperties, FC } from "react";

export type ProgressBarColor = "danger" | "warning" | "success" | "info";

/** Status values compatible with antd Progress API */
export type ProgressBarStatus = "normal" | "success" | "exception" | "active";

/** Size values for the progress bar */
export type ProgressBarSize = "small" | "default";

export interface ProgressBarProps {
  /** Progress value from 0 to 100 (can also use `percent` as an alias) */
  value?: number;
  /** Progress value from 0 to 100 (alias for `value`, for antd compatibility) */
  percent?: number;
  /** Color variant of the progress bar */
  color?: ProgressBarColor;
  /** Status of the progress bar (alternative to color, for antd compatibility) */
  status?: ProgressBarStatus;
  /** Whether to show the percentage text next to the progress bar */
  showInfo?: boolean;
  /** Size of the progress bar */
  size?: ProgressBarSize;
  /** Custom color for the progress bar (CSS color value) */
  strokeColor?: string;
  /** Additional class name for the container */
  className?: string;
}

const COLOR_CLASSES: Record<ProgressBarColor, string> = {
  danger: "bg-red-500",
  warning: "bg-yellow-500",
  success: "bg-green-500",
  info: "bg-blue-500",
};

const STATUS_TO_COLOR: Record<ProgressBarStatus, ProgressBarColor> = {
  normal: "info",
  success: "success",
  exception: "danger",
  active: "info",
};

const SIZE_CLASSES: Record<ProgressBarSize, { bar: string; text: string }> = {
  small: { bar: "h-1.5", text: "text-xs" },
  default: { bar: "h-2", text: "text-sm" },
};

export const ProgressBar: FC<ProgressBarProps> = ({
  value,
  percent,
  color,
  status,
  showInfo = false,
  size = "default",
  strokeColor,
  className,
}) => {
  // Support both `value` and `percent` props (percent takes precedence for antd compatibility)
  const progressValue = percent ?? value ?? 0;
  const clampedValue = Math.max(0, Math.min(100, progressValue));
  const widthStyle: CSSProperties = {
    width: `${clampedValue}%`,
    ...(strokeColor ? { backgroundColor: strokeColor } : {}),
  };

  // Determine color: strokeColor > color > status-mapped color > default
  const resolvedColor = color ?? (status ? STATUS_TO_COLOR[status] : "info");
  const sizeClasses = SIZE_CLASSES[size];

  // Add animation for active status
  const isActive = status === "active";

  return (
    <div className={cn("flex w-full items-center gap-2", className)}>
      <div
        className={cn(
          "flex-1 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700",
          sizeClasses.bar
        )}
      >
        <div
          className={cn(
            "h-full transition-all duration-300",
            !strokeColor && COLOR_CLASSES[resolvedColor],
            isActive && "animate-pulse"
          )}
          style={widthStyle}
        />
      </div>
      {showInfo && (
        <span
          className={cn(
            "min-w-[3ch] text-right text-gray-600 dark:text-gray-400",
            sizeClasses.text
          )}
        >
          {Math.round(clampedValue)}%
        </span>
      )}
    </div>
  );
};

ProgressBar.displayName = "ProgressBar";
